import React, { useState, useEffect } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import icons from "../../iconsList.json";
// DİKKAT: react-beautiful-dnd yerine @hello-pangea/dnd
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

// Yardımcı fonksiyon: Liste sırasını yeniden düzenleyen fonksiyon.
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const ModuleEdit = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Modül bilgisi ve form state
  const [moduleInfo, setModuleInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    icon: "",
    single_name: "",
    single_slug: "",
    multiple_name: "",
    multiple_slug: "",
    elements: [],
    showTable: false,
    hasCategory: false,
    categoryType: "none",
  });
  const [loading, setLoading] = useState(false);
    const language = useUserLanguage();
    const { translations } = useTranslations(language);
    const getText = (key, defaultValue) => translations[key] || defaultValue;

  // Modallar
  const [showAddElementModal, setShowAddElementModal] = useState(false);
  const [showEditElementModal, setShowEditElementModal] = useState(false);
  // İkon Seçimi Modalı
  const [showIconModal, setShowIconModal] = useState(false);

  // Yeni element ekleme (maxWidth / maxHeight eklendi)
  const [newElement, setNewElement] = useState({
    type: "text",
    name: "",
    label: "",
    placeholder: "",
    required: false,
    table: false,
    maxWidth: "",
    maxHeight: "",
    content_values: "",
    content_titles: "",
  });

  // Düzenlenecek element (maxWidth / maxHeight da taşıyabilir)
  const [editingElement, setEditingElement] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // İkon Arama & Listeleme
  const [iconSearch, setIconSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(300);

  // Arama metni değişince visibleCount'u sıfırla
  useEffect(() => {
    setVisibleCount(300);
  }, [iconSearch]);

  // Filtrelenmiş ikon listesi
  const filteredIcons = icons.filter((iconName) =>
    iconName.toLowerCase().includes(iconSearch.toLowerCase())
  );

  // Sayfa açıldığında veya slug değişince modül bilgisini çek
  useEffect(() => {
    if (!slug) return;
    fetchModuleData(slug);
  }, [slug]);

  // Modül bilgisini getiren fonksiyon
  const fetchModuleData = async (slugParam) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/modules/by-slug/${slugParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      setModuleInfo(data);

      // Elements'i parse edelim
      let parsedElements = [];
      if (data.elements) {
        try {
          parsedElements = JSON.parse(data.elements);
        } catch (err) {
          parsedElements = [];
        }
      }
      const cat  = data.category === 1 || data.category === "1";
      const mult = data.multiple_category === 1 || data.multiple_category === "1";
      // State'e aktar
      setFormData({
        name: data.name || "",
        icon: data.icon || "",
        single_name: data.single_name || "",
        single_slug: data.single_slug || "",
        multiple_name: data.multiple_name || "",
        multiple_slug: data.multiple_slug || "",
        elements: parsedElements,
        categoryType: !cat ? "none" : mult ? "multiple" : "single",
        showTable: false,
      });
    } catch (err) {
      console.error("Modül bilgisi alınamadı:", err);
      alert("Modül bilgisi alınamadı!");
    } finally {
      setLoading(false);
    }
  };

  // Form alanı değişikliği
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleNewChange = ({ target }) => {
    const { name, value, type, checked } = target;
    setNewElement((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  // Yeni element modalındaki inputlar
  const handleNewElementChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewElement((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Yeni element ekle
  const handleAddElement = () => {
    // ID ekleyerek kaydediyoruz
    const elementToAdd = { ...newElement, id: Date.now() };
    setFormData((prev) => ({
      ...prev,
      elements: [...prev.elements, elementToAdd],
    }));
    // Formu sıfırla
    setNewElement({
      type: "text",
      name: "",
      label: "",
      placeholder: "",
      required: false,
      table: false,
      maxWidth: "",
      maxHeight: "",
      content_values: "",
      content_titles: "",
    });
    setShowAddElementModal(false);
  };
  

  // Elementi sil (opsiyonel buton eklemediyseniz size kalmış)
  const handleRemoveElement = (index) => {
    setFormData((prev) => ({
      ...prev,
      elements: prev.elements.filter((_, i) => i !== index),
    }));
  };

  // Düzenleme için modalı aç
  const handleEditElement = (index) => {
    const element = formData.elements[index];
    setEditingElement({ ...element });
    setEditingIndex(index);
    setShowEditElementModal(true);
  };

  // Düzenlemeyi kaydet
  const handleUpdateElement = () => {
    const updatedElements = [...formData.elements];
    updatedElements[editingIndex] = editingElement;
    setFormData((prev) => ({ ...prev, elements: updatedElements }));
    setEditingElement(null);
    setEditingIndex(null);
    setShowEditElementModal(false);
  };

  // Drag & Drop tamamlandığında çağrılır
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = reorder(
      formData.elements,
      result.source.index,
      result.destination.index
    );
    setFormData((prev) => ({ ...prev, elements: newOrder }));
  };

  // Formu kaydet
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!moduleInfo) {
      alert("Henüz modül bilgisi yüklenmedi!");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const { categoryType, showTable, ...rest } = formData;
      // Tabloda görünsün mü? -> table alanı
      const enhancedElements = formData.elements.map((el) => ({
        ...el,
        table: typeof el.table === "boolean" ? el.table : showTable,
      }));
      const catFlag   = categoryType === "single" || categoryType === "multiple";
  const multFlag  = categoryType === "multiple";
      const payload = {
        ...rest,
        elements: JSON.stringify(enhancedElements),
        category:          catFlag  ? "1" : "0",
        multiple_category: multFlag ? "1" : "0",
      };

      // PUT isteği
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/modules/${moduleInfo.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Güncellendi:", response.data);
      alert("Modül başarıyla güncellendi!");
      // navigate(`/modules/${slug}`);
    } catch (err) {
      console.error("Modül güncellenemedi:", err);
      alert("Güncelleme başarısız!");
    } finally {
      setLoading(false);
    }
  };

  // İkon seçildiğinde formData.icon güncelleniyor, modal kapanıyor
  const handleIconSelect = (iconName) => {
    setFormData((prev) => ({ ...prev, icon: iconName }));
    setShowIconModal(false);
  };

  // "İkon Seç" butonuna tıklanınca icon modalı aç
  const openIconModal = () => {
    setShowIconModal(true);
  };

  if (loading && !moduleInfo) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <div className="container mt-4">
      <div className="card p-3 shadow-sm">
        <h4 className="mb-4">Modül: {formData.name}</h4>
        <form onSubmit={handleSubmit}>
          {/* Ana Bilgiler */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label fw-bold">{getText("MODULE_NAME_LABEL")}</label>
              <input
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">{getText("MODULE_ICON_LABEL")}</label>
              <div className="input-group">
                <input
                  name="icon"
                  className="form-control"
                  value={formData.icon}
                  onChange={handleChange}
                  placeholder="örn: home.svg"
                />
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openIconModal}
                >
                  {getText("ICON_SELECT_TITLE")}
                </button>
              </div>
              {formData.icon && (
                <div className="mt-2">
                  <img
                   src={`${process.env.PUBLIC_URL}/icons/svg/${formData.icon}`}
                    alt={formData.icon}
                    style={{ width: 30, height: 30 }}
                  />
                  <span className="ms-2">{formData.icon}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tekil / Çoğul alanlar */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label fw-bold">{getText("MODULE_SINGLE_NAME_LABEL")}</label>
              <input
                name="single_name"
                className="form-control"
                value={formData.single_name}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">{getText("MODULE_SINGLE_SLUG_LABEL")}</label>
              <input
                name="single_slug"
                className="form-control"
                value={formData.single_slug}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label fw-bold">{getText("MODULE_MULTIPLE_NAME_LABEL")}</label>
              <input
                name="multiple_name"
                className="form-control"
                value={formData.multiple_name}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">{getText("MODULE_MULTIPLE_SLUG_LABEL")}</label>
              <input
                name="multiple_slug"
                className="form-control"
                value={formData.multiple_slug}
                onChange={handleChange}
              />
            </div>
          </div>

          <Form.Label className="fw-bold">{getText("MODULE_CATEGORY_TYPE_LABEL")}</Form.Label>
 <Form.Select
   name="categoryType"
   value={formData.categoryType}
   onChange={handleChange}
 >
   <option value="none">{getText("CATEGORY_NONE")}</option>
   <option value="single">{getText("CATEGORY_SINGLE")}</option>
   <option value="multiple">{getText("CATEGORY_MULTIPLE")}</option>
 </Form.Select>

          {/* Element Ekle */}
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => {
                setNewElement({
                  type: "text",
                  name: "",
                  label: "",
                  placeholder: "",
                  required: false,
                  table: false,
                  maxWidth: "",
                  maxHeight: "",
                  content_values: "",
                  content_titles: "",
                });
                setShowAddElementModal(true);
              }}
            >
             {getText("ADD_ELEMENT_BTN")}
            </button>
          </div>

          {/* Element Listesi (Drag & Drop) */}
          {formData.elements.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="elementsDroppable">
                {(provided) => (
                  <ul
                    className="list-group mb-3"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {formData.elements.map((el, idx) => {
                      const draggableId = el.id ? String(el.id) : `element-${idx}`;
                      return (
                        <Draggable key={draggableId} draggableId={draggableId} index={idx}>
                          {(provided) => (
                            <li
                              className="list-group-item d-flex align-items-center"
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              {/* Sol tarafta sürükleme tutacağı */}
                              <div
                                {...provided.dragHandleProps}
                                style={{ cursor: "grab", marginRight: "1rem" }}
                              >
                                <svg width="20" height="20" viewBox="0 0 100 80">
                                  <rect width="100" height="10"></rect>
                                  <rect y="30" width="100" height="10"></rect>
                                  <rect y="60" width="100" height="10"></rect>
                                </svg>
                              </div>

                              {/* Elemanın metin kısmı — marginRight: "auto" ile bu kısmı sola yaslıyoruz */}
                              <div style={{ marginRight: "auto" }}>
                                <strong>{el.type.toUpperCase()}</strong>{" "}
                                <em>({el.name})</em> – {el.label}{" "}
                                {el.placeholder && `(${el.placeholder})`}
                                {el.required && <span className="ms-1 text-danger">*</span>}

                                {/* Sadece image/images için width/height göstermek isterseniz */}
                                {(el.type === "image" || el.type === "images") && (el.maxWidth || el.maxHeight) && (
                                  <span className="ms-2 text-muted">
                                    (Max: {el.maxWidth || "?"} x {el.maxHeight || "?"} px)
                                  </span>
                                )}

                                {typeof el.table !== "undefined" && (
                                  <span className="ms-2 badge bg-info">
                                    {el.table ? getText("FIELD_SHOW_TABLE") : getText("FIELD_NOT_IN_TABLE")}
                                  </span>
                                )}
                              </div>
                              {(el.type === "select" || el.type === "multiselect") && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="ms-3"
                                    onClick={() => {
                                      /* slug parametresini zaten useParams ile alıyorsunuz */
                                      navigate(`/admin/modules/edit/${slug}/select/${el.name}`);
                                    }}
                                  >
                                    {getText("MODAL_ADD_ELEMENT_BUTTON")}
                                  </Button>
                                )}
                              {/* Düzenle butonu */}
                              <div className="hstack gap-2 justify-content-end" style={{ paddingRight: '20px' }}>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="ms-3"
                                  onClick={() => handleEditElement(idx)}
                                >
                                  {getText("EDIT")}
                                </Button>
                                
                              </div>
                            </li>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* Kaydet Butonu */}
          <div className="d-flex justify-content-center">
            <button type="submit" className="btn btn-primary" disabled={loading}>
            {getText("SAVE_BTN")}
            </button>
          </div>
        </form>
      </div>

      {/* Modal: Yeni Element Ekle */}
      <Modal show={showAddElementModal} onHide={() => setShowAddElementModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{getText("MODAL_ADD_ELEMENT_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{getText("ELEMENT_TYPE_LABEL")}</Form.Label>
            <Form.Select
              name="type"
              value={newElement.type}
              onChange={handleNewElementChange}
            >
            <option value="text">{getText("FIELD_TYPE_TEXT")}</option>
            <option value="textarea">{getText("FIELD_TYPE_TEXTAREA")}</option>
            <option value="editor">{getText("FIELD_TYPE_EDITOR")}</option>
            <option value="shortDate">{getText("FIELD_TYPE_SHORT_DATE")}</option>
            <option value="longDate">{getText("FIELD_TYPE_LONG_DATE")}</option>
            <option value="image">{getText("FIELD_TYPE_IMAGE")}</option>
            <option value="images">{getText("FIELD_TYPE_IMAGES")}</option>
            <option value="file">{getText("FIELD_TYPE_FILE")}</option>
            <option value="files">{getText("FIELD_TYPE_FILES")}</option>
            <option value="select">{getText("FIELD_TYPE_SELECT")}</option>
            <option value="multiselect">{getText("FIELD_TYPE_MULTISELECT")}</option>

            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{getText("ELEMENT_NAME_LABEL")}</Form.Label>
            <Form.Control
              name="name"
              value={newElement.name}
              onChange={handleNewElementChange}
              placeholder="Örn: titleInput"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{getText("ELEMENT_LABEL_LABEL")}</Form.Label>
            <Form.Control
              name="label"
              value={newElement.label}
              onChange={handleNewElementChange}
              placeholder={getText("FIELD_LABEL_PLACEHOLDER")}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{getText("ELEMENT_PLACEHOLDER_LABEL")}</Form.Label>
            <Form.Control
              name="placeholder"
              value={newElement.placeholder}
              onChange={handleNewElementChange}
              placeholder={getText("FIELD_INPUT_PLACEHOLDER")}
            />
          </Form.Group>

          {/* Sadece "image" / "images" ise maxWidth / maxHeight alanlarını gösteriyoruz */}
          {(newElement.type === "image" || newElement.type === "images") && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{getText("ELEMENT_MAX_WIDTH_LABEL")}</Form.Label>
                <Form.Control
                  type="number"
                  name="maxWidth"
                  value={newElement.maxWidth}
                  onChange={handleNewElementChange}
                  placeholder="Örn: 800"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{getText("ELEMENT_MAX_HEIGHT_LABEL")}</Form.Label>
                <Form.Control
                  type="number"
                  name="maxHeight"
                  value={newElement.maxHeight}
                  onChange={handleNewElementChange}
                  placeholder="Örn: 600"
                />
              </Form.Group>
            </>
          )}

          <Form.Check
            type="checkbox"
            id="requiredCheckAdd"
            label={getText("ELEMENT_REQUIRED_LABEL")}
            name="required"
            checked={newElement.required}
            onChange={handleNewElementChange}
          />
          <Form.Check
            type="checkbox"
            id="tableCheckAdd"
            label={getText("ELEMENT_TABLE_LABEL")}
            name="table"
            checked={newElement.table}
            onChange={handleNewElementChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddElementModal(false)}>
          {getText("CANCEL_BTN")}
          </Button>
          <Button variant="primary" onClick={handleAddElement}>
          {getText("ADD_BTN")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Element Düzenleme */}
      <Modal show={showEditElementModal} onHide={() => setShowEditElementModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{getText("MODAL_EDIT_ELEMENT_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingElement && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{getText("ELEMENT_TYPE_LABEL")}</Form.Label>
                <Form.Select name="type" value={editingElement.type} disabled>
                <option value="text">{getText("FIELD_TYPE_TEXT")}</option>
                <option value="textarea">{getText("FIELD_TYPE_TEXTAREA")}</option>
                <option value="editor">{getText("FIELD_TYPE_EDITOR")}</option>
                <option value="shortDate">{getText("FIELD_TYPE_SHORT_DATE")}</option>
                <option value="longDate">{getText("FIELD_TYPE_LONG_DATE")}</option>
                <option value="image">{getText("FIELD_TYPE_IMAGE")}</option>
                <option value="images">{getText("FIELD_TYPE_IMAGES")}</option>
                <option value="file">{getText("FIELD_TYPE_FILE")}</option>
                <option value="files">{getText("FIELD_TYPE_FILES")}</option>
                <option value="select">{getText("FIELD_TYPE_SELECT")}</option>
                <option value="multiselect">{getText("FIELD_TYPE_MULTISELECT")}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{getText("ELEMENT_NAME_LABEL")}</Form.Label>
                <Form.Control
                  name="label"
                  value={editingElement.label}
                  onChange={(e) =>
                    setEditingElement((prev) => ({ ...prev, label: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{getText("ELEMENT_LABEL_LABEL")}</Form.Label>
                <Form.Control
                  name="placeholder"
                  value={editingElement.placeholder}
                  onChange={(e) =>
                    setEditingElement((prev) => ({ ...prev, placeholder: e.target.value }))
                  }
                />
              </Form.Group>

              {/* Eğer "image" veya "images" ise boyut alanlarını gösterelim */}
              {(editingElement.type === "image" || editingElement.type === "images") && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>{getText("ELEMENT_MAX_WIDTH_LABEL")}</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxWidth"
                      value={editingElement.maxWidth || ""}
                      onChange={(e) =>
                        setEditingElement((prev) => ({ ...prev, maxWidth: e.target.value }))
                      }
                      placeholder="Örn: 800"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>{getText("ELEMENT_MAX_HEIGHT_LABEL")}</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxHeight"
                      value={editingElement.maxHeight || ""}
                      onChange={(e) =>
                        setEditingElement((prev) => ({ ...prev, maxHeight: e.target.value }))
                      }
                      placeholder="Örn: 600"
                    />
                  </Form.Group>
                </>
              )}

              <Form.Check
                type="checkbox"
                id="requiredCheckEdit"
                label={getText("ELEMENT_REQUIRED_LABEL")}
                name="required"
                checked={editingElement.required}
                onChange={(e) =>
                  setEditingElement((prev) => ({ ...prev, required: e.target.checked }))
                }
              />
              <Form.Check
                type="checkbox"
                id="tableCheckEdit"
                label={getText("ELEMENT_TABLE_LABEL")}
                name="table"
                checked={editingElement.table}
                onChange={(e) =>
                  setEditingElement((prev) => ({ ...prev, table: e.target.checked }))
                }
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditElementModal(false)}>
          {getText("CANCEL_BTN")}
          </Button>
          <Button variant="primary" onClick={handleUpdateElement}>
            {getText("SAVE_BTN")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: İkon Seç */}
      <Modal
        show={showIconModal}
        onHide={() => setShowIconModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{getText("ICON_SELECT_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input
            type="text"
            className="form-control mb-3"
            placeholder={getText("ICON_SEARCH_PLACEHOLDER")}
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
          />
          <div
            className="row"
            style={{ maxHeight: "400px", overflowY: "auto" }}
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              if (scrollHeight - scrollTop - clientHeight < 50) {
                setVisibleCount((prev) => Math.min(prev + 30, filteredIcons.length));
              }
            }}
          >
            {filteredIcons.slice(0, visibleCount).map((iconName) => (
              <div
                key={iconName}
                className="col-3 text-center mb-3"
                style={{ cursor: "pointer" }}
                onClick={() => handleIconSelect(iconName)}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/icons/svg/${iconName}`}
                  alt={iconName}
                  style={{ width: 40, height: 40 }}
                />
                <div style={{ fontSize: "0.8rem", marginTop: 5 }}>
                  {iconName}
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIconModal(false)}>
          {getText("CLOSE_BTN")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ModuleEdit;
